import React from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  FileText, 
  Shield, 
  TrendingUp,
  Calendar,
  Download,
  Upload
} from 'lucide-react';

const Compliance = () => {
  const complianceOverview = {
    overallScore: 92,
    totalRequirements: 28,
    completed: 26,
    pending: 2,
    overdue: 0
  };

  const frameworks = [
    {
      name: 'TCFD (Task Force on Climate-related Financial Disclosures)',
      status: 'compliant',
      score: 95,
      lastUpdated: '2024-12-15',
      nextReview: '2025-06-15'
    },
    {
      name: 'EU Taxonomy Regulation',
      status: 'compliant',
      score: 88,
      lastUpdated: '2024-11-30',
      nextReview: '2025-05-30'
    },
    {
      name: 'CSRD (Corporate Sustainability Reporting Directive)',
      status: 'pending',
      score: 76,
      lastUpdated: '2024-10-20',
      nextReview: '2025-04-20'
    },
    {
      name: 'SBTi (Science Based Targets initiative)',
      status: 'compliant',
      score: 91,
      lastUpdated: '2024-12-01',
      nextReview: '2025-12-01'
    }
  ];

  const requirements = [
    {
      framework: 'TCFD',
      requirement: 'Climate Risk Assessment',
      status: 'completed',
      dueDate: '2024-12-31',
      progress: 100
    },
    {
      framework: 'EU Taxonomy',
      requirement: 'Taxonomy Alignment Report',
      status: 'completed',
      dueDate: '2024-11-30',
      progress: 100
    },
    {
      framework: 'CSRD',
      requirement: 'Double Materiality Assessment',
      status: 'pending',
      dueDate: '2025-03-31',
      progress: 65
    },
    {
      framework: 'SBTi',
      requirement: 'Scope 3 Emissions Report',
      status: 'pending',
      dueDate: '2025-02-28',
      progress: 45
    }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'compliant':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'overdue':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'default' as const,
      compliant: 'default' as const,
      pending: 'secondary' as const,
      overdue: 'destructive' as const
    };
    return (
      <Badge variant={variants[status as keyof typeof variants] || 'outline'}>
        {status}
      </Badge>
    );
  };

  return (
    <MainLayout>
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">Compliance Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Monitor regulatory compliance and framework adherence
          </p>
        </div>

        {/* Compliance Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overall Score</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceOverview.overallScore}%</div>
              <Progress value={complianceOverview.overallScore} className="mt-2" />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceOverview.completed}</div>
              <p className="text-xs text-muted-foreground">
                of {complianceOverview.totalRequirements} requirements
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending</CardTitle>
              <Clock className="h-4 w-4 text-yellow-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceOverview.pending}</div>
              <p className="text-xs text-muted-foreground">Action required</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overdue</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{complianceOverview.overdue}</div>
              <p className="text-xs text-muted-foreground">Immediate attention</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="frameworks" className="space-y-6">
          <TabsList>
            <TabsTrigger value="frameworks">Compliance Frameworks</TabsTrigger>
            <TabsTrigger value="requirements">Active Requirements</TabsTrigger>
            <TabsTrigger value="documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="frameworks">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {frameworks.map((framework, index) => (
                <Card key={index}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{framework.name}</CardTitle>
                        <CardDescription className="mt-1">
                          Last updated: {new Date(framework.lastUpdated).toLocaleDateString()}
                        </CardDescription>
                      </div>
                      {getStatusBadge(framework.status)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-2">
                          <span>Compliance Score</span>
                          <span>{framework.score}%</span>
                        </div>
                        <Progress value={framework.score} />
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" />
                          Next Review
                        </span>
                        <span>{new Date(framework.nextReview).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm">
                          <FileText className="h-4 w-4 mr-1" />
                          View Details
                        </Button>
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4 mr-1" />
                          Report
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="requirements">
            <Card>
              <CardHeader>
                <CardTitle>Active Compliance Requirements</CardTitle>
                <CardDescription>
                  Track progress on pending compliance requirements
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {requirements.map((req, index) => (
                    <div key={index} className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(req.status)}
                          <div>
                            <h3 className="font-semibold">{req.requirement}</h3>
                            <p className="text-sm text-muted-foreground">{req.framework}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {getStatusBadge(req.status)}
                          <p className="text-sm text-muted-foreground mt-1">
                            Due: {new Date(req.dueDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Progress</span>
                          <span>{req.progress}%</span>
                        </div>
                        <Progress value={req.progress} />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="documents">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Compliance Documents</CardTitle>
                    <CardDescription>
                      Manage compliance reports and supporting documentation
                    </CardDescription>
                  </div>
                  <Button>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    'TCFD Climate Risk Assessment 2024.pdf',
                    'EU Taxonomy Alignment Report 2024.pdf',
                    'Scope 3 Emissions Inventory 2024.xlsx',
                    'Double Materiality Assessment Draft.docx'
                  ].map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-primary" />
                        <div>
                          <h3 className="font-medium">{doc}</h3>
                          <p className="text-sm text-muted-foreground">
                            Uploaded: {new Date().toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm">
                          <Download className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default Compliance;